import React, { useState, useEffect, useContext, useMemo } from "react";
import "./home.css";
import "./homeV2.css";
import { CartContextData } from "../../context/newsPaperContext";
import { UserDataContext } from "../../context/userContext";
import { httpGet, httpPost } from "../../components/helpers/httpMethods";
import SEO from "../../components/Layout/SEO";
import BannerSlot from "../../components/Banner/BannerSlot";
import BillsStrip from "../../components/Bills/BillsStrip";
import FollowButton from "../../components/Follow/FollowButton";
import { loadFollowing, useFollowing } from "../../components/Follow/follow";
import { openFree } from "../../components/Pay/readFree";
import { notPublishing } from "../../components/helpers/publishing";

// Home page (2026-10-28 redesign, prototype https://claude.ai/artifact/FbjHubE54m9fA5UbHNRrPy):
// today's front pages (hero), headlines from this morning's editions, newspapers (category chips, followed first),
// magazines (newest issue featured), books on a shelf, the archive. Actions are the old cards' exactly: Read now /
// Subscribe (onOpenModal "homeSub") / Read free (opens straight away, components/Pay/readFree.js); Punch, stale and
// no-longer-publishing paid titles show no button.
const LAGOS = "Africa/Lagos";
const lagosDay = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: LAGOS });
const todayLagos = () => lagosDay(new Date());
const latestOf = (p) => (p.editions && p.editions[0]) || null;
const dateOf = (p) => { const e = latestOf(p); return e ? new Date(e.publishedDate).getTime() : 0; };
const niceName = (n) => { const t = String(n || "").trim(); return t && t === t.toUpperCase() ? t.toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (m, a, b) => a + b.toUpperCase()) : t; };
const dayLabel = (d) => {
  if (!d) return "";
  const day = lagosDay(d);
  if (day === todayLagos()) return "Today";
  if (day === lagosDay(Date.now() - 864e5)) return "Yesterday";
  const days = (Date.now() - new Date(d).getTime()) / 864e5;
  return new Date(d).toLocaleDateString("en-GB", days < 300 ? { weekday: "short", day: "numeric", month: "short", timeZone: LAGOS } : { day: "numeric", month: "short", year: "numeric", timeZone: LAGOS });
};
const issueLabel = (d) => {
  if (!d) return "";
  const days = (Date.now() - new Date(d).getTime()) / 864e5;
  return new Date(d).toLocaleDateString("en-GB", days <= 60 ? { day: "numeric", month: "long", year: "numeric", timeZone: LAGOS } : { month: "long", year: "numeric", timeZone: LAGOS });
};
const isStale = (p) => {
  const e = latestOf(p);
  if (!e || p.subscriptionType === "one-off") return false;
  const days = (Date.now() - new Date(e.publishedDate).getTime()) / 864e5;
  return days > (p.subscriptionType === "weekly" ? 10 : p.subscriptionType === "monthly" ? 35 : 3);
};
const isRecent = (p) => {
  const e = latestOf(p);
  if (!e) return false;
  const days = (Date.now() - new Date(e.publishedDate).getTime()) / 864e5;
  return days <= (p.subscriptionType === "weekly" ? 10 : p.subscriptionType === "monthly" ? 35 : p.subscriptionType === "one-off" ? 99999 : 3);
};

function Skeleton({ n = 10 }) {
  return <div className="hm-grid" aria-hidden="true">{Array.from({ length: n }).map((_, i) => <div key={i} className="hm-skel" />)}</div>;
}

export default function Home(props) {
  const history = props.history;
  const { filterType, setfilterType, categoriesType, setCategoriesType, UserCatgories, frequencyType, setcartPage, onOpenModal, mySubs } = useContext(CartContextData);
  const { userIsLogin, checkUserIsLogin, GetUserInfoFromStorge } = useContext(UserDataContext);
  const signedIn = userIsLogin === true;

  const [loading, setLoading] = useState(true);
  const [publications, setPublications] = useState([]);
  const [stories, setStories] = useState([]);
  const [archiveDay, setArchiveDay] = useState("");
  // Followed papers as of page load (order); the live set drives the "Following" tags.
  const [followed, setFollowed] = useState(() => new Set());
  const liveFollowed = useFollowing();

  useEffect(() => {
    setcartPage(history.location.pathname);
    checkUserIsLogin();
    GetUserInfoFromStorge();
    localStorage.setItem("paidWithWallet", "false");
    httpGet("filtered_publication?type=all").then((res) => {
      setPublications(res && res.success && res.data ? res.data.publications || [] : []);
      setLoading(false);
    }).catch(() => setLoading(false));
    httpGet("seo/front_stories").then((r) => setStories(r && r.data && Array.isArray(r.data.stories) ? r.data.stories : [])).catch(() => {});
    loadFollowing().then((ids) => setFollowed(new Set(ids)));
    return () => setcartPage("user/subscription");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const viewPublication = (id) => {
    history.push(`/view_paper/${id}`);
    httpPost("publication/view_count/", { id }).catch(() => {});
  };
  // The card's main action, as the old cards behaved, except (2026-10-29): always-free papers open straight away, and
  // a paid paper that has stopped publishing (helpers/publishing.js) has no Subscribe/Buy.
  const quietPaper = (p) => p.isPaid !== false && latestOf(p) && notPublishing(p, latestOf(p).publishedDate);
  const actionFor = (p, kind) => {
    const free = p.isPaid === false;
    const subscribed = signedIn && mySubs.includes(p.id);
    if (p.name === "PUNCH Newspaper" || !latestOf(p) || (!free && !subscribed && (isStale(p) || quietPaper(p)))) return null;
    const label = subscribed ? "Read now" : free ? "Read free" : kind === "book" ? "Buy now" : "Subscribe";
    const run = () => {
      if (!signedIn) return history.push("/login");
      if (subscribed) return kind === "newspaper" ? viewPublication(p.id) : history.push(`/view_paper/${p.id}`);
      if (!free) return kind === "book" ? onOpenModal(latestOf(p), "homeSub", "book") : onOpenModal(latestOf(p), "homeSub");
      return openFree({ publicationId: p.id, editionId: latestOf(p).id, history });
    };
    return { label, run, primary: !subscribed && !free };
  };

  const byType = useMemo(() => {
    let list = (publications || []).filter((p) => p.status === "active" && p.type && latestOf(p));
    if (categoriesType && categoriesType.id) list = list.filter((p) => p.categoryId === categoriesType.id);
    const followFirst = (arr) => (followed.size ? [...arr].sort((a, b) => (followed.has(String(b.id)) ? 1 : 0) - (followed.has(String(a.id)) ? 1 : 0)) : arr);
    const freq = (arr) => (frequencyType && frequencyType !== "all" ? arr.filter((p) => p.subscriptionType === frequencyType) : arr);
    const latest = (arr) => (filterType === "Latest" ? arr.filter(isRecent) : arr);
    const of = (t) => list.filter((p) => p.type.name === t);
    return {
      news: followFirst(latest(freq(of("newspapers")))),
      mags: latest(freq(of("magazines"))).sort((a, b) => dateOf(b) - dateOf(a)),
      books: latest(of("books")).sort((a, b) => dateOf(b) - dateOf(a)),
      todayNews: followFirst(of("newspapers").filter((p) => lagosDay(latestOf(p).publishedDate) === todayLagos())),
      recentNews: followFirst([...of("newspapers")].sort((a, b) => dateOf(b) - dateOf(a)).slice(0, 10)),
    };
  }, [publications, categoriesType, frequencyType, filterType, followed]);

  const show = (section) => filterType === "all" || filterType === "Latest" || (section === "news" && filterType === "Newspaper") || (section === "mags" && filterType === "Magazines") || (section === "books" && filterType === "Books");
  const home = filterType === "all";
  const showBanners = (filterType === "all" || filterType === "Latest") && !loading;
  const seeAll = (f) => { setfilterType(f); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const hero = byType.todayNews.length ? byType.todayNews : byType.recentNews;
  const now = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: LAGOS });

  const Card = ({ p, kind }) => {
    const a = actionFor(p, kind);
    const e = latestOf(p);
    const isNew = kind === "magazine" && e && (Date.now() - new Date(e.publishedDate).getTime()) / 864e5 <= 14;
    const catName = (UserCatgories || []).find((c) => c.id === p.categoryId);
    return (
      <article className="hm-card">
        <button type="button" className="hm-cover" onClick={() => viewPublication(p.id)} aria-label={`Open ${niceName(p.name)}`}>
          <img src={e.image} alt="" loading="lazy" />
          {signedIn && liveFollowed.has(String(p.id)) ? <span className="hm-tag">Following</span> : isNew ? <span className="hm-tag hm-tag-new">New</span> : null}
          {!p.isPaid ? <span className="hm-tag hm-tag-free">Free</span> : null}
          {quietPaper(p) ? <span className="hm-tag hm-tag-quiet">Not publishing</span> : null}
        </button>
        <div className="hm-card-foot">
          <div className="hm-card-text">
            <button type="button" className="hm-name" onClick={() => viewPublication(p.id)}>{niceName(p.name)}</button>
            <span className="hm-meta">{kind === "magazine" ? issueLabel(e.publishedDate) : dayLabel(e.publishedDate)}{kind === "newspaper" && catName ? ` · ${catName.name.charAt(0).toUpperCase()}${catName.name.slice(1)}` : ""}</span>
            {a ? <button type="button" className={`hm-act${a.primary ? " primary" : ""}`} onClick={a.run}>{a.label} ›</button> : null}
          </div>
          <FollowButton light id={p.id} name={niceName(p.name)} signedIn={signedIn} onSignIn={() => history.push("/login")} />
        </div>
      </article>
    );
  };

  const empty = (what) => (
    <div className="hm-empty">
      <p>No {what} match these filters.</p>
      <button type="button" onClick={() => { setCategoriesType({ id: null }); setfilterType("all"); }}>Show everything</button>
    </div>
  );

  const featured = byType.mags[0];
  const featuredAction = featured ? actionFor(featured, "magazine") : null;

  return (
    <div className="hm">
      <SEO
        title="TheNewspaperStand: Read Nigerian Newspapers & Magazines Online"
        description="Read Nigerian newspapers and magazines online. Today’s Vanguard, ThisDay, Daily Trust, Independent and more as full e-papers, old issues by date since 2020, and Change of Name notices. On any phone or computer."
        url="https://newstand.tech/"
      />

      {home ? (
        <section className="hm-hero" aria-labelledby="hm-hero-h">
          <div className="hm-wrap hm-hero-in">
            <div className="hm-hero-top">
              <div className="hm-hero-copy">
                <p className="hm-live"><span aria-hidden="true" />{now}{byType.todayNews.length ? ` · ${byType.todayNews.length} paper${byType.todayNews.length === 1 ? "" : "s"} out today` : ""}</p>
                <h1 id="hm-hero-h">{byType.todayNews.length || loading ? "Today’s front pages." : "The latest front pages."}</h1>
                <p className="hm-hero-sub">Every major Nigerian paper, the moment it’s printed. Read the whole edition on your phone, tablet or computer.</p>
              </div>
              {!signedIn ? (
                <div className="hm-hero-ctas">
                  <a className="hm-btn hm-btn-accent" href="#hm-newspapers">Start reading</a>
                  <button type="button" className="hm-btn hm-btn-ghost" onClick={() => history.push("/create_account")}>Create a free account</button>
                </div>
              ) : null}
            </div>
            <div className="hm-shelf" role="list" aria-label="Today’s front pages">
              {loading ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="hm-shelf-item hm-skel-dark" />) : hero.map((p) => (
                <button type="button" role="listitem" key={p.id} className="hm-shelf-item" onClick={() => viewPublication(p.id)}>
                  <span className="hm-shelf-cover"><img src={latestOf(p).image} alt={`${niceName(p.name)} front page`} /></span>
                  <span className="hm-shelf-cap"><span>{niceName(p.name)}</span><em>{signedIn && liveFollowed.has(String(p.id)) ? "Following" : dayLabel(latestOf(p).publishedDate)}</em></span>
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {home ? <div className="hm-banner hm-banner-top"><BannerSlot spot="home_top" inline /></div> : null}

      {home && stories.length ? (
        <section className="hm-stories" aria-labelledby="hm-stories-h">
          <div className="hm-wrap">
            <div className="hm-head"><h2 id="hm-stories-h" className="hm-h2-sm">On the front pages</h2><span className="hm-note">Headlines from this morning’s editions</span></div>
            <div className="hm-story-grid">
              {stories.slice(0, 4).map((s) => (
                <button type="button" key={s.editionId} className="hm-story" onClick={() => history.push(s.url)}>
                  {s.cover ? <img src={s.cover} alt="" loading="lazy" /> : null}
                  <span className="hm-story-text">
                    <span className="hm-story-paper">{s.paper}</span>
                    <span className="hm-story-h">{s.text}</span>
                    <span className="hm-story-link">Read in {s.paper} ›</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {show("news") ? (
        <section id="hm-newspapers" className="hm-sec" aria-labelledby="hm-news-h">
          <div className="hm-wrap">
            <div className="hm-head">
              <div><h2 id="hm-news-h">Newspapers</h2>{signedIn && followed.size ? <p className="hm-sub">Papers you follow come first.</p> : null}</div>
            </div>
            {UserCatgories && UserCatgories.length ? (
              <div className="hm-chips" role="group" aria-label="Category">
                <button type="button" className={`hm-chip${!categoriesType || categoriesType.id === null ? " on" : ""}`} aria-pressed={!categoriesType || categoriesType.id === null} onClick={() => setCategoriesType({ id: null })}>All</button>
                {UserCatgories.map((c) => (
                  <button type="button" key={c.id} className={`hm-chip${categoriesType && categoriesType.id === c.id ? " on" : ""}`} aria-pressed={!!(categoriesType && categoriesType.id === c.id)} onClick={() => setCategoriesType(c)}>
                    {c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                  </button>
                ))}
              </div>
            ) : null}
            {loading ? <Skeleton /> : byType.news.length ? (
              <div className="hm-grid">{byType.news.map((p) => <Card key={p.id} p={p} kind="newspaper" />)}</div>
            ) : empty("newspapers")}
          </div>
        </section>
      ) : null}

      {home && !loading ? <div className="hm-wrap"><BillsStrip history={history} /></div> : null}
      {showBanners && show("news") ? <div className="hm-banner"><BannerSlot spot="home_between:newspapers" inline /></div> : null}

      {show("mags") ? (
        <section className="hm-sec hm-grey" aria-labelledby="hm-mag-h">
          <div className="hm-wrap">
            <div className="hm-head">
              <div><h2 id="hm-mag-h">Magazines</h2><p className="hm-sub">Style, society, business and faith, issue by issue.</p></div>
              {home && byType.mags.length > 7 ? <button type="button" className="hm-seeall" onClick={() => seeAll("Magazines")}>See all ›</button> : null}
            </div>
            {loading ? <Skeleton n={6} /> : !byType.mags.length ? empty("magazines") : home ? (
              <div className="hm-mag">
                <article className="hm-feature">
                  <button type="button" className="hm-feature-cover" onClick={() => viewPublication(featured.id)} aria-label={`Open ${niceName(featured.name)}`}>
                    <img src={latestOf(featured).image} alt="" />
                  </button>
                  <div className="hm-feature-text">
                    {(Date.now() - dateOf(featured)) / 864e5 <= 14 ? <span className="hm-pill">New issue</span> : <span className="hm-pill hm-pill-quiet">Latest issue</span>}
                    <h3>{niceName(featured.name)}</h3>
                    <p>Issue of {issueLabel(latestOf(featured).publishedDate)}</p>
                    <div className="hm-feature-btns">
                      {featuredAction ? <button type="button" className="hm-btn hm-btn-dark" onClick={featuredAction.run}>{featuredAction.label === "Read now" ? "Read this issue" : featuredAction.label}</button> : null}
                      <button type="button" className="hm-btn hm-btn-line" onClick={() => viewPublication(featured.id)}>See the magazine</button>
                    </div>
                  </div>
                </article>
                <div className="hm-grid hm-grid-sm">{byType.mags.slice(1, 7).map((p) => <Card key={p.id} p={p} kind="magazine" />)}</div>
              </div>
            ) : (
              <div className="hm-grid">{byType.mags.map((p) => <Card key={p.id} p={p} kind="magazine" />)}</div>
            )}
          </div>
        </section>
      ) : null}

      {showBanners && show("mags") ? <div className="hm-banner"><BannerSlot spot="home_between:magazines" inline /></div> : null}

      {show("books") ? (
        <section className="hm-sec" aria-labelledby="hm-book-h">
          <div className="hm-wrap">
            <div className="hm-head">
              <div><h2 id="hm-book-h">Books</h2><p className="hm-sub">Stories and guides from Nigerian authors. Buy once, keep forever.</p></div>
              {home && byType.books.length > 8 ? <button type="button" className="hm-seeall" onClick={() => seeAll("Books")}>See all ›</button> : null}
            </div>
            {loading ? <Skeleton n={6} /> : !byType.books.length ? empty("books") : (
              <div className={home ? "hm-books" : "hm-books hm-books-wrap"}>
                {(home ? byType.books.slice(0, 12) : byType.books).map((p) => {
                  const a = actionFor(p, "book");
                  return (
                    <article key={p.id} className="hm-book">
                      <button type="button" className="hm-book-cover" onClick={() => history.push(`/view_paper/${p.id}`)} aria-label={`Open ${niceName(p.name)}`}>
                        <img src={latestOf(p).image} alt="" loading="lazy" /><span aria-hidden="true" />
                      </button>
                      <button type="button" className="hm-name" onClick={() => history.push(`/view_paper/${p.id}`)}>{niceName(p.name)}</button>
                      {a ? <button type="button" className={`hm-act${a.primary ? " primary" : ""}`} onClick={a.run}>{a.label} ›</button> : null}
                    </article>
                  );
                })}
              </div>
            )}
            {home && byType.books.length ? <div className="hm-shelf-line" aria-hidden="true" /> : null}
          </div>
        </section>
      ) : null}

      {home ? (
        <section className="hm-archive-wrap" aria-labelledby="hm-arc-h">
          <div className="hm-archive">
            <div className="hm-archive-copy">
              <span className="hm-eyebrow">The archive</span>
              <h2 id="hm-arc-h">The paper from the day that mattered.</h2>
              <p>A birthday, a wedding, a big day in history. Pick a date and keep that edition for good.</p>
            </div>
            <form className="hm-archive-form" onSubmit={(e) => { e.preventDefault(); history.push(archiveDay ? `/archive?date=${archiveDay}` : "/archive"); }}>
              <label>Pick a day<input type="date" value={archiveDay} max={todayLagos()} min="1960-01-01" onChange={(e) => setArchiveDay(e.target.value)} /></label>
              <button type="submit" className="hm-btn hm-btn-accent">Find that day</button>
            </form>
          </div>
        </section>
      ) : null}

      {showBanners ? <div className="hm-banner hm-banner-last"><BannerSlot spot="home_bottom" inline /></div> : null}
    </div>
  );
}
